import React, { createContext, useContext, useState, useEffect } from 'react';
import { themes, defaultTheme } from '../css-styles/themeConfig';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Try to get theme from localStorage, fallback to default
    const savedTheme = localStorage.getItem('tiritix-theme');
    return savedTheme && themes[savedTheme] ? savedTheme : defaultTheme;
  });

  // Function to dynamically load CSS
  const loadThemeCSS = (themeName) => {
    // Remove existing theme stylesheets
    const existingThemeLinks = document.querySelectorAll('link[data-theme]');
    existingThemeLinks.forEach(link => link.remove());

    // Load base reset CSS if not already loaded
    if (!document.querySelector('link[data-css="reset"]')) {
      const resetLink = document.createElement('link');
      resetLink.rel = 'stylesheet';
      resetLink.href = `/css-styles/base/reset.css`;
      resetLink.setAttribute('data-css', 'reset');
      document.head.appendChild(resetLink);
    }

    // Load the new theme CSS
    if (themes[themeName]) {
      const themeLink = document.createElement('link');
      themeLink.rel = 'stylesheet';
      themeLink.href = `/css-styles/themes/${themes[themeName].cssFile}`;
      themeLink.setAttribute('data-theme', themeName);
      document.head.appendChild(themeLink);
    }
  };

  // Change theme function
  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('tiritix-theme', themeName);
      loadThemeCSS(themeName);
    }
  };

  // Load initial theme on mount
  useEffect(() => {
    loadThemeCSS(currentTheme);
  }, []);

  // Update theme when currentTheme changes
  useEffect(() => {
    loadThemeCSS(currentTheme);
  }, [currentTheme]);

  const value = {
    currentTheme,
    themes,
    changeTheme,
    themeData: themes[currentTheme]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
